import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

type FAQCategory = "applicants" | "reviewers";

// FAQ data for college applicants
const applicantFAQData: FAQItem[] = [
  {
    question: "How does the AI essay review work?",
    answer:
      "Our AI analyzes your essay using advanced natural language processing to evaluate structure, content, authenticity, and writing quality. It provides detailed feedback with specific suggestions for improvement, similar to what an experienced college counselor would offer.",
  },
  {
    question:
      "What makes CampusQuest different from other college application tools?",
    answer:
      "CampusQuest is your comprehensive AI copilot for the entire college application process. While others focus on just essay editing, we provide end-to-end support including deadline tracking, brainstorming tools, application management, and community features. Our AI understands college admissions holistically, not just writing mechanics.",
  },
  {
    question: "How many essays can I review with the starter plan?",
    answer:
      "The starter plan includes 5 AI essay reviews to help you get started. This gives you a chance to experience our AI copilot's feedback system and see how it can transform your writing before upgrading for unlimited access to all features.",
  },
  {
    question: "Can I trust the AI feedback for my college applications?",
    answer:
      "Our AI is trained on thousands of successful college essays and admissions best practices. While AI feedback is highly valuable for structure, clarity, and content improvement, we recommend using it as a powerful first step before getting human feedback from counselors or teachers for final review.",
  },
  {
    question: "What types of essays does CampusQuest support?",
    answer:
      "CampusQuest supports all types of college application essays including Common App personal statements, supplemental essays, scholarship essays, and transfer essays. Our AI understands the specific requirements and expectations for different essay types.",
  },
  {
    question: "How quickly do I get feedback on my essays?",
    answer:
      "You get feedback instantly! Once you submit your essay, our AI analyzes it and provides comprehensive feedback within seconds. Pro users get priority processing for even faster response times.",
  },
  {
    question: "Is my essay data secure and private?",
    answer:
      "Absolutely. We take privacy seriously and use enterprise-grade security to protect your essays. Your personal essays are never shared, stored longer than necessary, or used to train our models without explicit consent. We comply with all data protection regulations.",
  },
  {
    question: "What's the difference between monthly and full cycle billing?",
    answer:
      "We offer two billing options: Monthly billing charges $10 every month and automatically renews until you cancel. Full cycle billing is a one-time payment of $32 ($8/month for 4 months) that provides Pro access through January 2025, perfect for students completing their college application cycle. The full cycle option offers a 20% discount and doesn't auto-renew.",
  },
  {
    question: "Can I cancel my Pro subscription anytime?",
    answer:
      "Yes, you can cancel your Pro subscription at any time through your account settings or our customer portal. You'll continue to have Pro access until the end of your current billing period, and then your account will revert to the free plan.",
  },
];

// FAQ data for reviewers
const reviewerFAQData: FAQItem[] = [
  {
    question: "How do I become a reviewer on CampusQuest?",
    answer:
      "To become a reviewer, click on the 'Become a Reviewer' button in the navigation bar. You'll complete a short onboarding process where we'll ask about your academic background, experience, and areas of expertise. Once approved, you'll have access to the reviewer dashboard where you can start reviewing essays.",
  },
  {
    question: "What qualifications do I need to be a reviewer?",
    answer:
      "We welcome reviewers who are current college students, recent graduates, or professionals with experience in college admissions, writing, or education. Strong writing skills, attention to detail, and familiarity with the college application process are essential. Experience with different types of application essays is a plus.",
  },
  {
    question: "How much time do I need to commit as a reviewer?",
    answer:
      "The time commitment is flexible and based on your availability. You can choose how many essays you want to review each week. Each review typically takes 30-45 minutes, depending on the essay length and complexity. You can set your availability in your reviewer dashboard.",
  },
  {
    question: "How am I compensated for my reviews?",
    answer:
      "Reviewers are compensated based on the number and quality of reviews they complete. Payment rates vary depending on essay type, length, and turnaround time. Detailed compensation information is provided during the onboarding process and is visible in your reviewer dashboard.",
  },
  {
    question: "What kind of support will I receive as a reviewer?",
    answer:
      "We provide comprehensive resources to help you succeed, including review guidelines, sample feedback, and access to a community of experienced reviewers. Our team is also available to answer questions and provide guidance as needed.",
  },
  {
    question: "How are reviews assigned to me?",
    answer:
      "Essays are matched to reviewers based on your expertise, availability, and the specific needs of the student. You'll receive notifications when new essays are available for review, and you can choose which ones to accept based on your schedule and comfort with the topic.",
  },
  {
    question: "What happens if I can't complete a review on time?",
    answer:
      "We understand that unexpected situations arise. If you can't complete a review by the deadline, you can release it back to the pool so another reviewer can take it. However, consistently missing deadlines may affect your reviewer rating and future assignments.",
  },
  {
    question: "How is the quality of my reviews evaluated?",
    answer:
      "Reviews are evaluated based on thoroughness, helpfulness, clarity, and adherence to our guidelines. Students can also rate their reviews, which contributes to your overall reviewer rating. High-quality reviews lead to more assignments and potential bonuses.",
  },
];

const FAQItem: React.FC<{
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ item, isOpen, onToggle }) => {
  return (
    <Card
      className="border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer bg-white"
      onClick={onToggle}
    >
      <CardContent className="p-0">
        <div className="w-full p-6 text-left">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 pr-4">
              {item.question}
            </h3>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
            )}
          </div>
        </div>
        {isOpen && (
          <div className="px-6 pb-6">
            <p className="text-gray-600 leading-relaxed">{item.answer}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FAQSection: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [activeCategory, setActiveCategory] =
    useState<FAQCategory>("applicants");

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  // Get the current FAQ data based on the active category
  const currentFAQData =
    activeCategory === "applicants" ? applicantFAQData : reviewerFAQData;

  return (
    <section id="faq-section" className="py-24 px-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="h-8 w-8 text-gray-700 mr-3" />
            <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
              Support
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about CampusQuest, whether you're
            applying to college or helping others as a reviewer.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white">
            <button
              onClick={() => setActiveCategory("applicants")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeCategory === "applicants"
                  ? "bg-gray-900 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              For Applicants
            </button>
            <button
              onClick={() => setActiveCategory("reviewers")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeCategory === "reviewers"
                  ? "bg-gray-900 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              For Reviewers
            </button>
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {currentFAQData.map((item, index) => (
            <FAQItem
              key={index}
              item={item}
              isOpen={openItems.has(index)}
              onToggle={() => toggleItem(index)}
            />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center pt-16 border-t border-gray-200">
          <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Still have questions?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Can't find the answer you're looking for? Please chat with our
              friendly team and we'll be happy to help.
            </p>
            <button
              onClick={() => {
                const contactButton = document.querySelector(
                  "[data-contact-button]"
                );
                if (contactButton) {
                  (contactButton as HTMLButtonElement).click();
                } else {
                  window.location.href =
                    "mailto:team@essaypilot.io?subject=Support Question";
                }
              }}
              className="bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
